import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { FeatureFlag } from '../../../shared/models/enums/feature-flag';
import { MatchGroupService } from '../../match-groups/match-group.service';
import { FacilitySearchResult } from '../../models/facility-search-result.model';
import { MatchGroupPermissions } from '../../models/match-group-permissions.model';
import { MatchGroupsResponse } from '../../models/match-group.model';
import { ComplianceClient } from '../compliance-client.service';
import { ComplianceGroupResponse } from '../models/compliance-group-response.model';
import { FacilityResponse } from '../models/facility-response.model';
import { MatchGroupPermissionsDto } from '../models/match-group-permissions-dto.model';
import { NSwagMigrationService } from './nswag-migration.service';

// 7 endpoints
@Injectable({ providedIn: 'root' })
export class MatchGroupNSwagMigrationService extends NSwagMigrationService {
    protected readonly className = 'MatchGroupNSwagMigrationService';
    protected readonly featureFlag = FeatureFlag.NSwagMigrationMatchGroupService;
    private readonly legacy = inject(MatchGroupService);
    private readonly complianceClient = inject(ComplianceClient);

    // ** ComplianceController.GetComplianceGroup
    loadMatchGroups(): Observable<MatchGroupsResponse | ComplianceGroupResponse> {
        return this.invoke(x => x
            ? this.legacy.loadMatchGroups()
            : this.complianceClient.getComplianceGroup());
    }

    // ** ComplianceController.GetFacilitiesAndSystems
    loadFacilities(type: string, name: string): Observable<FacilitySearchResult | FacilityResponse> {
        return this.invoke(x => x
            ? this.legacy.loadFacilities(type, name)
            : this.complianceClient.getFacilitiesAndSystems(type, name));
    }

    // ** ComplianceController.CreateGroup
    createGroup(name: string, facilityIds: number[], systemIds: number[]): Observable<unknown | void> {
        return this.invoke(x => x
            ? this.legacy.createGroup(name, facilityIds, systemIds)
            : this.complianceClient.createGroup(name, { facilityIds, systemIds } as any));
    }

    // ** ComplianceController.UpdateGroup
    updateGroup(
        id: number,
        name: string,
        facilityIds: number[],
        systemIds: number[],
        isStrike: boolean
    ): Observable<unknown | void> {
        return this.invoke(x => x
            ? this.legacy.updateGroup(id, name, facilityIds, systemIds, isStrike)
            : this.complianceClient.updateGroup(id, { facilityIds, systemIds, isStrike, groupName: name } ));
    }

    // ** ComplianceController.DeleteGroupItem
    deleteGroupItem(facilityId: number, groupId: number, isStrike: boolean): Observable<unknown | ComplianceGroupResponse> {
        return this.invoke(x => x
            ? this.legacy.deleteGroupItem(facilityId, groupId, isStrike)
            : this.complianceClient.deleteGroupItem(facilityId, groupId, isStrike));
    }

    // ** ComplianceController.DeleteGroup
    deleteGroup(groupId: number): Observable<unknown | ComplianceGroupResponse> {
        return this.invoke(x => x
            ? this.legacy.deleteGroup(groupId)
            : this.complianceClient.deleteGroup(groupId));
    }

    // ** ComplianceController.GetComplianceGroupPermissions
    getPermissions(): Observable<MatchGroupPermissions | MatchGroupPermissionsDto> {
        return this.invoke(x => x
            ? this.legacy.getPermissions()
            : this.complianceClient.getComplianceGroupPermissions());
    }
}
